// This component is responsible for displaying and managing the list of users.

import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import {
  TContext,
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
  TUser,
} from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import DateUtils from "../../../utils/date";
import { ePrivileges, eREGIONS, eDATE_FORMAT, eResultCode } from "../../../utils/enum";
import styles from "../entities/entity.module.css";
import UserForm from "./form";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  CONFIG_GROUPS,
  dateOnlyFormat,
  defaultFilterParams,
  ROLEUNIQUEID,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import GenericPopover from "../../../components/pop-over";
import {
  Button,
  Tag,
  Space,
  Input,
  Table,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import CustomDrawer from "../../../components/drawer";
import {
  AddEditUser,
  GetConfigParamDataList,
  GetUserList,
} from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import utils from "../../../utils";
import CustomModal from "../../../components/modal";
import ActionCard from "../../../components/actionCard/actionCard";
import {
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  TeamOutlined,
  DeleteOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import { useColumnSearch } from "../../../components/table/column-search";
import { get } from "lodash";

const USER_LIST_COLUMN_KEY = "userList";

/*
 * UserList Component
 * This component is responsible for displaying and managing the list of users.
 * It handles fetching user data, searching, filtering, and rendering the user list.
 */
export default function UserList() {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TUser[]>([]);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const context: TContext = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user as TUser);
  const [stakeHolderName, setStakeHolderName] = useState<string>(
    activeUser?.stakeHolderName || "",
  );
  const [selectedUser, setSelectedUser] = useState<TUser | null>(null); // State to keep track of selected user for modal
  const [stakeHolderId, setStakeHolderId] = useState<number | null>(null); // State to keep track of selected user for modal
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const { getDateRangeFilterProps } = useColumnSearch<TUser>();

  useEffect(() => {
    setActiveUser(context?.user as TUser);
    setStakeHolderName(context?.user?.stakeHolderName as string);
  }, [context?.user]);

  useEffect(() => {
    const fetchDetails = async () => {
      const stakeHolderTypes = await fetchConfigParams(
        CONFIG_GROUPS.STAKEHOLDERS,
      );

      const activeStakeHolder = stakeHolderTypes.find(
        (item: TDropdownOptions) =>
          item?.data?.paramUniqueId === stakeHolderName,
      );

      if (activeStakeHolder) {
        setStakeHolderId(activeStakeHolder.value);
      }
    };
    fetchDetails();
  }, [stakeHolderName]);

  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload,
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const showDeleteModal = (user: TUser) => {
    setSelectedUser(user);
    setIsDeleteModalVisible(true);
  };

  const deleteUser = async (user: TUser) => {
    try {
      const payload = {
        data: {
          id: user.id,
          isDeleted: 1,
          stakeHolderId: stakeHolderId,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const showModal = (user: TUser) => {
    setSelectedUser(user);
    setIsModalVisible(true);
  };

  const handleOk = (userDetails: TUser) => {
    updateUserDetails(userDetails);
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleDeleteOk = async () => {
    if (selectedUser) {
      await deleteUser(selectedUser);
    }
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "displayName",
      key: "displayName",
      // width: "30%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.displayName?.localeCompare(b.displayName ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.displayName?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.emailId?.localeCompare(b.emailId ?? "") || 0,
      filters: utils.getUniqueFilters(data, "emailId"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.emailId?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Mobile No.",
      dataIndex: "mobileNo",
      key: "mobileNo",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.mobileNo?.localeCompare(b.mobileNo ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.mobileNo?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Role",
      dataIndex: "userRoleName",
      key: "userRoleName",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.userRoleName?.localeCompare(b.userRoleName ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.userRoleName?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      ...getDateRangeFilterProps("createdOn"),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, context?.user?.dateFormat); // Apply date format here
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: "1" },
        { text: "In Active", value: "0" },
      ],
      // width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        (a.isActive?.toString() || "").localeCompare(
          b.isActive?.toString() || "",
        ), // Handle undefined case
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.isActive?.toString() === value,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "var(--bs-primary)" : "#f50"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      width: 80,
      render: (user: TUser) => (
        <>
          {context?.user?.roles?.[0]?.roleUniqueId !==
            ROLEUNIQUEID.SUPERADMIN &&
          user?.userRoleUniqueId === ROLEUNIQUEID.SUPERADMIN ? null : (
            <GenericPopover
              placement="left"
              content={
                <div className="standardActionBox">
                  {hasAddAccess() && (
                    <>
                      <CustomDrawer
                        size="large"
                        trigger={
                          <ActionCard text="Edit">
                            <EditOutlined />
                          </ActionCard>
                        }
                        component={UserForm}
                        componentProps={{
                          id: user.id,
                          activeStake: stakeHolderName,
                          onRefreshList: () => {
                            fetchData();
                          },
                        }}
                        title="User Form"
                      />
                      <ActionCard
                        text={user.isActive ? "Inactive" : "Active"}
                        onClick={() => showModal(user)}
                      >
                        {user.isActive ? (
                          <UserDeleteOutlined />
                        ) : (
                          <UserAddOutlined />
                        )}
                      </ActionCard>
                    </>
                  )}

                  {hasDeleteAccess() && (
                    <ActionCard
                      text="Delete"
                      onClick={() => showDeleteModal(user)}
                    >
                      <DeleteOutlined />
                    </ActionCard>
                  )}
                </div>
              }
              icon={
                <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
              }
            />
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
    const updatedColumns = initialColumns.filter(
      (col) => col.key !== "orgName",
    );
    if (stakeHolderName !== STAKE_HOLDERS.IDEX) {
      const columnsToAdd: any = [
        {
          title: "Organization",
          dataIndex: "orgName",
          key: "orgName",
          width: "20%",
          filterSearch: true,
          sorter: (a: TUser, b: TUser) =>
            a.orgName?.localeCompare(b.orgName ?? "") || 0,
          filters: utils.getUniqueFilters(data, "orgName"),
          onFilter: (value: string | number | boolean, record: TUser) =>
            record.orgName?.includes(value as string),
        },
      ];

      updatedColumns.splice(4, 0, ...columnsToAdd);
    }
    setColumns(updatedColumns);
    setDraftColumns(updatedColumns);
  }, [stakeHolderName]);

  // const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [columns, setColumns] = useState(initialColumns); // Actual saved columns state
  const [draftColumns, setDraftColumns] = useState(columns); // Staging state for changes

  useEffect(() => {
    (async () => {
      const savedPreferences =
        await loadColumnPreferences(USER_LIST_COLUMN_KEY);
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col]),
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const updateUserDetails = async (values: TUser) => {
    try {
      const { isActive, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isActive: isActive ? 1 : 0,
          stakeHolderId: stakeHolderId,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // To fetch the list of all the users
  const fetchData = async () => {
    if (stakeHolderName) {
      try {
        setIsLoading(true);
        const requestPayload = {
          data: {
            stakeHolderName,
            pageSize: -1,
            currentPage: 1,
          },
        };
        const response: TResponseModel = await post(
          GetUserList,
          requestPayload,
        );
        const { data, dataResponse, filterModel } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const formattedData = data.map((item: TUser, index: number) => ({
            key: (item.id ?? "").toString(),
            srNo: index + 1,
            displayName: item.displayName,
            emailId: item.emailId,
            mobileNo: item.mobileNo,
            userRoleName: item.userRoleName,
            orgName: item.orgName,
            createdOn: item.createdOn,
            status: item?.isActive ? "Active" : "In Active",
            isActive: item.isActive,
            id: item.id,
            customerName: item?.customerName,
            userRoleUniqueId: item?.userRoleUniqueId,
          }));
          setData(formattedData);
          setFilterParams({ ...(filterModel as TFilterModel) });
          setIsLoading(false);
          updateColumnFilters(data, activeFilters, searchText);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const updateColumnFilters = (
    dataset: TUser[],
    currentFilters: Record<string, any>,
    currentSearchText: string,
  ) => {
    const getFilters = (key: keyof TUser) => {
      const validRowsForThisColumn = dataset.filter((item) => {
        // Global search match
        const searchMatch =
          !currentSearchText ||
          Object.values(item).some((val) =>
            String(val).toLowerCase().includes(currentSearchText.toLowerCase()),
          );

        // Check each active filter
        const filterMatches = Object.entries(currentFilters).every(
          ([filterKey, filterValue]) => {
            if (!filterValue || filterValue.length === 0 || filterKey === key)
              return true;

            // Date Range Match (nested dayjs arrays)
            if (
              filterValue.length > 0 &&
              Array.isArray(filterValue[0]) &&
              filterValue[0].length === 2
            ) {
              const startDayjs = filterValue[0][0];
              const endDayjs = filterValue[0][1];
              if (startDayjs && endDayjs && "valueOf" in startDayjs) {
                const start = new Date(startDayjs.valueOf()).setHours(
                  0,
                  0,
                  0,
                  0,
                );
                const end = new Date(endDayjs.valueOf()).setHours(
                  23,
                  59,
                  59,
                  999,
                );
                const itemDateVal = item[filterKey as keyof TUser];
                if (!itemDateVal) return false;
                const rowDate = new Date(itemDateVal as string).getTime();
                return rowDate >= start && rowDate <= end;
              }
            }

            const column = [...initialColumns, ...columns].find(
              (col) => col.key === filterKey || col.dataIndex === filterKey,
            );
            if (column && column.onFilter) {
              return filterValue.some((val: any) =>
                column.onFilter!(val, item),
              );
            }

            const itemValue = item[filterKey as keyof TUser];
            return filterValue.includes(itemValue);
          },
        );

        return searchMatch && filterMatches;
      });

      const uniqueValues = Array.from(
        new Set(
          validRowsForThisColumn
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-",
            ) as string[],
        ),
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };

    const nameFilters = getFilters("displayName");
    const emailFilters = getFilters("emailId");
    const mobileNoFilters = getFilters("mobileNo");
    const userRoleNameFilters = getFilters("userRoleName");
    const orgNameFilters = getFilters("orgName");
    const customerFilters = getFilters("customerName");

    const statusFilters = [
      { text: "Active", value: "1" },
      { text: "In Active", value: "0" },
    ].filter((option) =>
      dataset.some((item) => item.isActive?.toString() === option.value),
    );

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "displayName") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "emailId") {
          return { ...col, filters: emailFilters };
        }
        if (col.key === "mobileNo") {
          return { ...col, filters: mobileNoFilters };
        }
        if (col.key === "userRoleName") {
          return { ...col, filters: userRoleNameFilters };
        }
        if (col.key === "orgName") {
          return { ...col, filters: orgNameFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerFilters };
        }
        if (col.key === "isActive") {
          return { ...col, filters: statusFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    updateColumnFilters(data, activeFilters, searchText);
  }, [activeFilters, data, searchText]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const globalsearchMatch =
      searchText === "" ||
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchText.toLowerCase()),
      );

    // Dynamic check for all active filters
    const filterMatches = Object.entries(activeFilters).every(
      ([filterKey, filterValue]) => {
        if (!filterValue || filterValue.length === 0) return true;

        // Date Range Match (nested dayjs arrays)
        if (
          filterValue.length > 0 &&
          Array.isArray(filterValue[0]) &&
          filterValue[0].length === 2
        ) {
          const startDayjs = filterValue[0][0];
          const endDayjs = filterValue[0][1];
          if (startDayjs && endDayjs && "valueOf" in startDayjs) {
            const start = new Date(startDayjs.valueOf()).setHours(0, 0, 0, 0);
            const end = new Date(endDayjs.valueOf()).setHours(23, 59, 59, 999);
            const itemDateVal = item[filterKey as keyof TUser];
            if (!itemDateVal) return false;
            const rowDate = new Date(itemDateVal as string).getTime();
            return rowDate >= start && rowDate <= end;
          }
        }

        // Dropdown or Search Match
        const column = [...initialColumns, ...columns].find(
          (col) => col.key === filterKey || col.dataIndex === filterKey,
        );
        if (column && column.onFilter) {
          return filterValue.some((val: any) => column.onFilter!(val, item));
        }

        const itemValue = item[filterKey as keyof TUser];
        return filterValue.includes(itemValue);
      },
    );

    return globalsearchMatch && filterMatches;
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(USER_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(USER_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item))),
  );

  const formatExportRow = (user: any) => {
    const { password, ...rest } = user;
    const mapped = {
      ...rest,
      isActive: user.isActive ? "Active" : "Inactive",
      createdOn: DateUtils.formatForExport(
        user.createdOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
      updatedOn: DateUtils.formatForExport(
        user.updatedOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
    };

    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  };

  const exportData = data.map(formatExportRow);

  const exportFilteredData = filteredData.map(formatExportRow);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  useEffect(() => {
    handleResetAll();
  }, [stakeHolderName]);

  const hasAddAccess = () => {
    if (stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.ADDEDITCUSTUSERS,
      );
    } else if (stakeHolderName === STAKE_HOLDERS.IDEX) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.ADDEDITIDEXUSERS,
      );
    }
    return false;
  };
  const hasDeleteAccess = () => {
    if (stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.DELETECUSTUSERS,
      );
    } else if (stakeHolderName === STAKE_HOLDERS.IDEX) {
      return Utils.isUserHasAccess(
        activeUser?.privilegeList as any,
        ePrivileges.DELETEIDEXUSERS,
      );
    }
    return false;
  };

  return (
    <>
      <div>
        <div className={styles.parentContainer}>
          {Utils.isUserHasAccess(
            (activeUser?.privilegeList as TPrivilege[]) || [],
            ePrivileges.VIEWIDEXUSERS,
          ) && (
            <div
              className={`${styles.tab} ${
                stakeHolderName === STAKE_HOLDERS.IDEX
                  ? styles.activeTab
                  : styles.inactiveTab
              }`}
              onClick={() => {
                setStakeHolderName(STAKE_HOLDERS.IDEX);
                setFilterParams(defaultFilterParams);
              }}
            >
              <span
                className={
                  stakeHolderName === STAKE_HOLDERS.IDEX
                    ? styles.activeText
                    : styles.inactiveText
                }
              >
                {activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
                  ? "Internal"
                  : "Idex"}
              </span>
            </div>
          )}

          {Utils.isUserHasAccess(
            (activeUser?.privilegeList as TPrivilege[]) || [],
            ePrivileges.VIEWCUSTUSERS,
          ) && (
            <div
              className={`${styles.tab} ${
                stakeHolderName === STAKE_HOLDERS.CUSTOMER
                  ? styles.activeTab
                  : styles.inactiveTab
              }`}
              onClick={() => {
                setStakeHolderName(STAKE_HOLDERS.CUSTOMER);
                setFilterParams(defaultFilterParams);
              }}
            >
              <span
                className={
                  stakeHolderName === STAKE_HOLDERS.CUSTOMER
                    ? styles.activeText
                    : styles.inactiveText
                }
              >
                {activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                  ? "Internal"
                  : "Customer"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="pageBody">
        <PageHeader
          icon={<TeamOutlined />}
          title={
            stakeHolderName === STAKE_HOLDERS.IDEX
              ? `${filterParams?.filterRowsCount} Total Idex Users`
              : stakeHolderName === STAKE_HOLDERS.CUSTOMER
                ? `${filterParams?.filterRowsCount} Total Customer Users`
                : ""
          }
          size="large"
        >
          <Space className="headerItems" wrap>
            {hasAddAccess() && (
              <CustomDrawer
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Add
                  </Button>
                }
                component={UserForm}
                componentProps={{
                  id: 0,
                  activeStake: stakeHolderName,
                  onRefreshList: () => {
                    fetchData();
                  },
                }}
                title="Dealer Form"
                size="large"
              />
            )}

            <Input
              placeholder="Filter by keywords"
              value={searchText}
              onChange={handleSearch}
              style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
              prefix={<SearchOutlined />}
            />
            <ExportToCSV
              data={exportData}
              filteredData={exportFilteredData}
              allColumns={initialColumns}
              visibleColumns={visibleColumns}
              moduleName="UserList"
            />
            <Tooltip title="Reset Filters">
              <FilterOutlined onClick={handleResetAll} />
            </Tooltip>
            <Tooltip title="Configure Columns">
              <OrderedListOutlined
                onClick={() => {
                  setDraftColumns(columns);
                  setDrawerVisible(true);
                }}
              />
            </Tooltip>
          </Space>
        </PageHeader>

        <div className="tableWrap">
          <Table
            columns={visibleColumns}
            dataSource={filteredData}
            loading={isLoading}
            pagination={{
              // ...pagination,
              position: ["bottomLeft"],
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total: any, range: any[]) =>
                `Showing ${range[0]}-${range[1]} of ${total} total entries`,
            }}
            onChange={handleTableChange}
            bordered={false}
            size="small"
            // sticky
            scroll={{ x: "max-content" }}
          />
        </div>
        <Drawer
          title="Customize Columns"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          style={{ transition: "transform 0.3s ease-in-out" }}
        >
          <ColumnManager
            columns={draftColumns}
            initialColumns={initialColumns}
            onDragEnd={handleDragEnd}
            onToggleColumn={toggleColumn}
            onResetColumns={handleResetColumns}
            onSave={handleSaveColumns}
            onCancel={() => setDrawerVisible(false)}
          />
        </Drawer>
        <CustomModal
          title="Confirm"
          isOpen={isModalVisible} // Ensure selectedUserId is valid
          okText="Yes"
          cusHeader={true}
          cancelText="No"
          onOk={() =>
            handleOk({
              ...selectedUser,
              isActive: !selectedUser?.isActive,
            } as any)
          }
          onCancel={() => handleCancel()}
        >
          <p>
            You are about to{" "}
            {selectedUser?.isActive ? "Deactivate" : "Activate"}{" "}
            <b>{selectedUser?.displayName}</b>
          </p>
        </CustomModal>
        <CustomModal
          title="Confirm Delete"
          isOpen={isDeleteModalVisible}
          onOk={handleDeleteOk}
          onCancel={() => handleDeleteCancel()}
          okText="Yes"
          cancelText="No"
          cusHeader={true}
        >
          <p>
            Are you about to delete <b>{selectedUser?.displayName}</b>?
          </p>
        </CustomModal>
      </div>
    </>
  );
}
